import { Box, Button, Icon, SearchField, Text } from '@factorialco/deprecated-design-system'
import { AtsCandidateTagsFragment } from 'generated/resources/graphql'
import { useToggle } from 'usehooks-ts'

import PopoverMenu from 'modules/core/components/PopoverMenu'
import ScrollableList from 'modules/core/components/ScrollableList'
import { useMemo } from 'modules/core/hooks/useMemo'
import { useDebouncedSearch } from 'modules/core/hooks/useSearch'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'
import { matchText, normalizeText } from 'modules/core/lib/stringUtils'

import { useAddTagToCandidate } from 'modules/ats/hooks/candidates/useAddTagToCandidate'
import { useTags } from 'modules/ats/hooks/tags/useTags'
import { useAtsContext } from 'modules/ats/lib/atsContext'

import * as styles from './index.vanilla.css'

export interface CandidateSource {
  source: 'Application' | 'Candidate'
}

interface Props extends CandidateSource {
  candidate: AtsCandidateTagsFragment
}

export const CandidateTagsSelector = ({ candidate, source }: Props) => {
  const { canManageAtsSettings } = useAtsContext()
  const { loading: loadingTags, tags } = useTags()
  const { loading: creating, addTag } = useAddTagToCandidate()
  const { search, debouncedSearch, onSearch } = useDebouncedSearch()
  const [isMenuOpen, toggleIsMenuOpen] = useToggle()

  const trackCreateTagEvent = () => track(trackingEvents.ats.createTag, { source })
  const trackAddTagToCandidateEvent = () => track(trackingEvents.ats.addTagToCandidate, { source })

  const onCreateTag = async () => {
    await addTag({
      atsTagName: search.trim(),
      atsCandidateId: candidate.id,
    })

    // Yeah, I know, pretty ugly, but that's the only way to reset the search
    onSearch({ target: { value: '' } } as React.ChangeEvent<HTMLInputElement>)
    trackCreateTagEvent()
    trackAddTagToCandidateEvent()
  }

  const handleOnChange = (value: unknown) => {
    if (creating) return

    const atsTagId = tags.find((tag) => tag.name === (value as string))?.id

    if (!atsTagId) {
      return
    }

    addTag({
      atsTagId,
      atsCandidateId: candidate.id,
    })
    trackAddTagToCandidateEvent()
  }

  const selectedTagNames = candidate.tags && candidate.tags.map((tag) => tag.name)
  const selectedTagNamesKey = selectedTagNames && selectedTagNames.join('')

  const filteredTags = useMemo(() => {
    return tags.filter((tag) => {
      if (selectedTagNames?.includes(tag.name)) {
        return false
      }

      return matchText(debouncedSearch, tag.name)
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, loadingTags, selectedTagNamesKey, creating])

  // TODO: Does this cover all cases? Should we expose the slug?
  const showCreateButton =
    canManageAtsSettings &&
    search.trim() &&
    !tags.some((tag) => normalizeText(tag.name) === normalizeText(search))

  return (
    <PopoverMenu type="dialog" onClick={toggleIsMenuOpen} open={isMenuOpen}>
      <PopoverMenu.Trigger>
        <Button.Decorative type="secondary" iconName="add">
          {i18n.t('ats.tags.add.button')}
        </Button.Decorative>
      </PopoverMenu.Trigger>
      <PopoverMenu.Content side="bottom" align="center" width="s360">
        <Box paddingX="s8">
          <Box
            border={{
              all: {
                color: 'grey400',
                width: 's1',
                style: 'solid',
              },
            }}
            borderRadius={{
              all: 'abs008',
            }}
            overflowX="hidden"
          >
            <Box width="full" padding="s8" background="grey200">
              <SearchField
                variant="big"
                search={search}
                onSearch={onSearch}
                placeholder={i18n.t('ats.tags.search.placeholder')}
                loading={search !== debouncedSearch}
              />
            </Box>
            <ScrollableList>
              {showCreateButton && (
                <button onClick={onCreateTag} className={styles.optionButton}>
                  <Icon.Medium icon="add" color="grey1000" />
                  <Text.Normal size="200" weight="medium" color="grey1000">
                    {i18n.t('ats.tags.create_tag', { tagName: search })}
                  </Text.Normal>
                </button>
              )}

              {filteredTags.map((tag) => (
                <button
                  key={tag.id}
                  onClick={() => handleOnChange(tag.name)}
                  className={styles.optionButton}
                >
                  <Text.Normal size="200" weight="medium" color="grey800">
                    {tag.name}
                  </Text.Normal>
                </button>
              ))}
            </ScrollableList>
          </Box>
        </Box>
      </PopoverMenu.Content>
    </PopoverMenu>
  )
}
